// Bootstrap tier: everything imported at the top loads on every page. Only what is
// exclusive to the launcher runtime defers, via loadLauncherRuntime.

import type { FeatureFlags } from '@/domain/configuration';
import { isRenderedLauncherType } from '@/domain/launcherTypes';
import { isLegacyUIEnabled } from '@/domain/legacyUiConfig';
import type { OpenWidgetAndSend } from '@/launchers/openWidgetAndSend';
import type { MountedLauncher } from '@/launchers/renderer';
import type { LauncherAppearanceSource } from '@/launchers/theme';
import type { V2Launcher } from '@/launchers/types';
import type { PagePromptsReader } from '@/lib/beacon/pagePrompts';
import { isLauncherVisible } from '@/lib/displayRules/isLauncherVisible';
import getNormalizedUrl from '@/lib/getNormalizedUrl';
import { logger } from '@/lib/logger';
import { normalizeLang } from '@/lib/normalizeLang';

export type LauncherRuntime = Pick<typeof import('@/launchers/launcherRuntime'), 'mountLauncher'>;

// The slice of the v2 server configuration the launchers read. V2Configuration
// satisfies it, so bootstrap hands that over whole.
export interface LauncherConfiguration {
  appearance?: LauncherAppearanceSource;
  behavior?: { lng?: string };
  launchers?: V2Launcher[];
  legacyUI?: boolean;
}

export interface LauncherDeps {
  // The org's dynamic-config flags: enableChatLaunchers gates the whole tier.
  // Required, so no caller switches every launcher off by omission.
  featureFlags: FeatureFlags | undefined;
  loadRuntime?: () => Promise<LauncherRuntime>;
  // Bootstrap owns the event emitter too, and it works before the widget loads.
  onWidgetClosed: (listener: () => void) => () => void;
  // Bootstrap owns the widget and the send; a renderer reaches both only through here.
  openWidgetAndSend: OpenWidgetAndSend;
  // Filled by the page-view path; an AI launcher draws from it.
  pagePrompts: PagePromptsReader;
}

let mounted: MountedLauncher[] = [];
let currentRun = 0;

let watched: { config: LauncherConfiguration; deps: LauncherDeps } | null = null;
let gatedUrl = '';

export async function startLaunchers(config: LauncherConfiguration | undefined, deps: LauncherDeps): Promise<void> {
  const loadRuntime = deps.loadRuntime ?? loadLauncherRuntime;
  // Ahead of the gate so an org that has no launchers also cancels a run still
  // waiting on the chunk. Counting is the whole cost of the unconfigured path.
  const run = ++currentRun;

  // Fail closed: an absent flag renders nothing, not every launcher already authored.
  // Legacy UI draws the corner bubble only; its launchers stay configured for Enhanced.
  if (!deps.featureFlags?.enableChatLaunchers || isLegacyUIEnabled({ v2: config }) || !config?.launchers?.length) {
    standDown();
    return;
  }

  // Narrowed ahead of the URL gate: an org whose launchers are all types this SDK
  // does not draw pays neither the navigation listeners nor the launcher chunk.
  const rendered = config.launchers.filter((launcher) => isRenderedLauncherType(launcher.type));
  if (!rendered.length) {
    standDown();
    return;
  }

  try {
    destroyMounted();
    watchNavigation(config, deps);

    const pageUrl = getNormalizedUrl();
    gatedUrl = pageUrl;
    const matching = rendered.filter((launcher) => isLauncherVisible(launcher, pageUrl));
    if (!matching.length) {
      return;
    }

    const { mountLauncher } = await loadRuntime();

    // A later run started while this one waited on the chunk; it owns the page now.
    if (run !== currentRun) {
      return;
    }

    // The shopper left while the chunk loaded: mounting now puts this page's launchers on the next page.
    if (getNormalizedUrl() !== pageUrl) {
      syncLaunchersToUrl();
      return;
    }

    // All at once: an AI launcher may wait for the page's answer, and a sibling must not queue behind it.
    await Promise.all(
      matching.map(async (launcher) => {
        // Per launcher: one that fails to mount must not cost its siblings the page.
        try {
          const handle = await mountLauncher(
            launcher,
            {
              launcherId: launcher.id,
              pageUrl,
              lang: normalizeLang(config.behavior?.lng),
              openWidgetAndSend: deps.openWidgetAndSend,
              onWidgetClosed: deps.onWidgetClosed,
              pagePrompts: deps.pagePrompts,
            },
            config.appearance,
          );
          if (!handle) {
            return;
          }
          // A launcher that waited can land after its run: a superseding run never saw
          // this handle, so nothing else tears it down.
          if (run !== currentRun) {
            handle.destroy();
            return;
          }
          // The shopper left during the wait: this launcher belongs to the page they were on.
          if (getNormalizedUrl() !== pageUrl) {
            handle.destroy();
            syncLaunchersToUrl();
            return;
          }
          mounted.push(handle);
        } catch (error) {
          logger.error(`[launchers] orchestrator failed to mount "${launcher.id}"`, error);
        }
      }),
    );
  } catch (error) {
    // The caller is init() on the brand's page: a throw here demotes the whole
    // org to the V1 widget, and an unhandled rejection surfaces in their console.
    logger.error('[launchers] orchestrator failed to start launchers', error);
  }
}

export function stopLaunchers(): void {
  // Stop means stop: a run still waiting on the chunk must not mount afterwards.
  ++currentRun;
  standDown();
}

// This page gets no launchers: forget the config and take down anything mounted.
function standDown(): void {
  unwatchNavigation();
  destroyMounted();
}

/**
 * Re-gate every launcher against the current URL, if it changed since the last gate.
 *
 * A launcher that still matches the new page is destroyed and recreated rather than
 * left alone: product A's questions must not stay on product B's page.
 */
export function syncLaunchersToUrl(): void {
  try {
    if (!watched || getNormalizedUrl() === gatedUrl) {
      return;
    }
    void startLaunchers(watched.config, watched.deps);
  } catch (error) {
    // Reached from Gladly.navigate() and from listeners on the brand's page: a throw
    // rejects their promise or surfaces in their console.
    logger.error('[launchers] orchestrator failed to re-gate on navigation', error);
  }
}

// No history.pushState patching on a brand's page: a framework's own pushState
// arrives through Gladly.navigate().
function watchNavigation(config: LauncherConfiguration, deps: LauncherDeps): void {
  watched = { config, deps };
  // A stable listener reference, so a repeat start registers nothing new.
  window.addEventListener('popstate', syncLaunchersToUrl);
  window.addEventListener('hashchange', syncLaunchersToUrl);
}

function unwatchNavigation(): void {
  watched = null;
  gatedUrl = '';
  window.removeEventListener('popstate', syncLaunchersToUrl);
  window.removeEventListener('hashchange', syncLaunchersToUrl);
}

function destroyMounted(): void {
  const handles = mounted;
  mounted = [];
  for (const handle of handles) {
    // Log-and-continue: one launcher's teardown must not leak the rest.
    try {
      handle.destroy();
    } catch (error) {
      logger.error('[launchers] orchestrator failed to destroy a launcher', error);
    }
  }
}

// A literal specifier: the static-closure checks that keep the runtime out of the
// always-loaded tier cannot follow a computed one.
function loadLauncherRuntime(): Promise<LauncherRuntime> {
  return import('@/launchers/launcherRuntime');
}
